/**
 * widgetTabsConfig.js
 * הגדרת טאבים + ברירות מחדל לכל רכיב
 *
 * כל ערך מכיל:
 *  - intro: { title, emoji, description }  — להצגה ב-header
 *  - defaultData: ערכי ברירת מחדל ל-formData (לרכיב חדש)
 *  - tabs: מערך של { id, label, componentName, props? }
 *
 * הטאב AppearanceTab (עיצוב) מתווסף אוטומטית בסוף לכל רכיב.
 * הטאב TemplateSplitStep (תבנית) מתווסף אוטומטית אם hasTemplate !== false.
 */

export const WIDGET_TABS_CONFIG = {

  // ─────────────────────────────────────────────
  InyaneiHaYomWidget: {
    intro: {
      title: 'ענייני היום',
      emoji: '📖',
      description: 'מציג את כל ענייני ההלכה והתפילה הרלוונטיים ליום.',
    },
    defaultData: {
      title: 'ענייני היום', showTitle: true,
      bgColor: '', borderType: 'default',
      edah: 'ashkenaz',
      showTachanun: true, showHallel: true, showPrayerAdditions: true,
      showSeasonalPrayers: true, showSeasonalTransitions: true, showSelichot: true,
      showWeekdayPrayers: true, showShabbatAdditions: true, showMotzaeiShabbat: true,
      showSeasonalCustoms: true, showLiturgy: true, showLeyning: false,
      showHaftarah: false, showMourningPeriods: true, showLimudim: false, lookaheadDays: 0,
    },
    tabs: [
      { id: 'settings', label: 'הגדרות', componentName: 'InyaneiSettingsStep' },
    ],
  },

  // ─────────────────────────────────────────────
  LimudimWidget: {
    intro: {
      title: 'לימודים יומיים',
      emoji: '📚',
      description: 'מציג את כל סדרי הלימוד היומיים.',
    },
    defaultData: {
      title: 'לימודים יומיים', showTitle: true,
      bgColor: '', borderType: 'default',
      showDafYomi: true, showMishnaYomi: true, showNachYomi: false,
      showRambam1: true, showRambam3: false,
      showYerushalmiVilna: false, showYerushalmiSchottenstein: false,
      showChofetzChaim: false, showShemiratHaLashon: false,
      showSeferHaMitzvot: false, showKitzurShulchanAruch: false,
      showArukhHaShulchanYomi: false, showPsalms: false,
      showTanakhYomi: false, showPirkeiAvotSummer: false,
      showPerekYomi: false, showDafWeekly: false, showDirshuAmudYomi: false,
    },
    tabs: [
      { id: 'settings', label: 'הגדרות', componentName: 'LimudimSettingsStep' },
    ],
  },

  // ─────────────────────────────────────────────
  SeatingMapWidget: {
    intro: {
      title: 'מפת מושבים',
      emoji: '🪑',
      description: 'מפה ויזואלית של מושבי בית הכנסת.',
    },
    defaultData: {
      title: 'מפת מושבים', showTitle: true,
      bgColor: '', borderType: 'default',
      mapData: null,
    },
    tabs: [
      { id: 'map', label: 'מפת מושבים', componentName: 'SeatingMapStep' },
    ],
  },

  // ─────────────────────────────────────────────
  DocumentWidget: {
    intro: {
      title: 'מסמך',
      emoji: '📄',
      description: 'מציג מסמך PDF או Word על המסך.',
    },
    defaultData: {
      bgColor: '', borderType: 'default',
      documentUrl: '', documentType: '', fileName: '',
      pageNumber: 1, autoScroll: false, scrollInterval: 10,
    },
    hasTemplate: false,
    tabs: [
      { id: 'document', label: 'העלאת מסמך', componentName: 'DocumentStep' },
    ],
  },

  // ─────────────────────────────────────────────
  OmerWidget: {
    intro: {
      title: 'ספירת העומר',
      emoji: '🌾',
      description: 'מציג את ספירת העומר עם ברכה וספירה מלאה.',
    },
    defaultData: {
      title: 'ספירת העומר', showTitle: false,
      bgColor: '', borderType: 'default',
      nusach: 'ashkenaz', showBracha: true, showFullNusach: true,
      showLeshemYichud: false, showPasuk: 'none',
      showAfterCount: false, showPreview: false,
    },
    tabs: [
      { id: 'settings', label: 'הגדרות', componentName: 'OmerSettingsStep' },
    ],
  },

  // ─────────────────────────────────────────────
  HalachicTimesWidget: {
    intro: {
      title: 'זמני היום ההלכתיים',
      emoji: '⏰',
      description: 'מציג את הזמנים ההלכתיים בלבד — בתצוגה נקייה ופשוטה.',
    },
    defaultData: {
      title: 'זמני היום ההלכתיים', showTitle: true,
      bgColor: '', borderType: 'default',
      visibleZmanim: null, showCountdown: true,
    },
    tabs: [
      { id: 'zmanimVisibility', label: 'זמנים', componentName: 'ZmanimVisibilityStep' },
    ],
  },

  // ─────────────────────────────────────────────
  ZmanimWidget: {
    intro: {
      title: 'ציר הזמן היומי',
      emoji: '🕰️',
      description: 'מציג את הזמנים ההלכתיים לאורך היום על ציר זמן חי.',
    },
    defaultData: {
      title: 'סדר היום', showTitle: true,
      bgColor: '', borderType: 'default',
      customTimes: [], visibleZmanim: null,
      tefilotColor: '#0369a1', moadColor: '#b45309', shabbatColor: '#7c3aed',
      showInyaneiHayom: false, inyaneiDisplayMode: 'section',
      inyaneiEdah: 'ashkenaz', inyaneiCategories: null,
    },
    tabs: [
      { id: 'zmanimVisibility', label: 'זמנים על הציר', componentName: 'ZmanimVisibilityStep' },
      { id: 'customTimes',      label: 'זמנים נוספים',  componentName: 'CustomTimesStep' },
      { id: 'inyanei',          label: 'ענייני היום',    componentName: 'ZmanimInyaneiStep' },
      { id: 'design',           label: 'צבעים',         componentName: 'ZmanimDesignStep' },
    ],
  },

  // ─────────────────────────────────────────────
  ShabbatWidget: {
    intro: {
      title: 'זמני שבת',
      emoji: '🕯️',
      description: 'מציג זמני כניסה ויציאת שבת.',
    },
    defaultData: {
      title: 'זמני שבת', showTitle: true,
      bgColor: '', borderType: 'default',
      shabbatPrayers: [], drashot: [],
      entryIcon: 'ShabbatCandles', exitIcon: 'MoonStar',
      entryZman: null, exitZman: null, showTimeRule: false, showEntryExit: true,
      showLeyning: false, showHaftarah: false, leyningDisplayMode: 'summary',
    },
    tabs: [
      { id: 'calc',     label: 'כניסה ויציאה',  componentName: 'ShabbatCalcStep' },
      { id: 'prayers',  label: 'תפילות שבת',    componentName: 'PrayerListStep', props: { mode: 'shabbat' } },
      { id: 'drashot',  label: 'דרשות',          componentName: 'DrashotStep' },
      { id: 'leyning',  label: 'קריאת תורה',     componentName: 'LeyningSettingsStep' },
    ],
  },

  // ─────────────────────────────────────────────
  MoadWidget: {
    intro: {
      title: 'זמני מועד',
      emoji: '📅',
      description: 'מציג זמני כניסה ויציאה לימי מועד.',
    },
    defaultData: {
      title: 'מועד', showTitle: true,
      bgColor: '', borderType: 'default',
      days: [], moadPrayers: [], moadDrashot: [],
      entryIcon: 'ShabbatCandles', exitIcon: 'MoonStar',
      entryZman: null, exitZman: null,
      showLeyning: false, showHaftarah: false, leyningDisplayMode: 'summary',
    },
    tabs: [
      { id: 'calc',     label: 'זמן כניסה',    componentName: 'MoadCalcStep' },
      { id: 'days',     label: 'ימי המועד',     componentName: 'MoadDaysStep' },
      { id: 'zmanim',   label: 'זמנים',         componentName: 'MoadZmanimPerDayStep' },
      { id: 'prayers',  label: 'תפילות מועד',   componentName: 'PrayerListStep', props: { mode: 'moad' } },
      { id: 'drashot',  label: 'דרשות',          componentName: 'MoadDrashotStep' },
      { id: 'leyning',  label: 'קריאת תורה',     componentName: 'LeyningSettingsStep' },
    ],
  },

  // ─────────────────────────────────────────────
  TefilotWidget: {
    intro: {
      title: 'תפילות יום חול',
      emoji: '🤲',
      description: 'מציג לוח זמני תפילות לימות החול.',
    },
    defaultData: {
      title: 'תפילות יום חול', showTitle: true,
      bgColor: '', borderType: 'default',
      customTimes: [], recurringDisplay: 'compact',
      showRuleMode: false, highlightNextMinyan: false, timelineValidFrom: '', timelineValidTo: '',
    },
    tabs: [
      { id: 'prayers',          label: 'תפילות',       componentName: 'PrayerListStep', props: { mode: 'tefilot' } },
      { id: 'timelineValidity', label: 'תוקף תצוגה',  componentName: 'TimelineValidityStep' },
    ],
  },

  // ─────────────────────────────────────────────
  TextWidget: {
    intro: {
      title: 'תוכן חופשי',
      emoji: '📝',
      description: 'תיבת טקסט להצגת תוכן קצר ונקודתי.',
    },
    defaultData: {
      bgColor: '', borderType: 'default',
      text: '',
    },
    showTitleSection: false,
    tabs: [
      { id: 'content', label: 'תוכן', componentName: 'TextContentStep' },
    ],
  },

  // ─────────────────────────────────────────────
  ImageWidget: {
    intro: {
      title: 'תמונה / לוגו',
      emoji: '🖼️',
      description: 'מציג תמונה או לוגו.',
    },
    defaultData: {
      bgColor: '', borderType: 'default',
      imageUrl: '',
    },
    hasTemplate: false,
    showTitleSection: false,
    tabs: [
      { id: 'image', label: 'בחירת תמונה', componentName: 'ImageStep' },
    ],
  },

  // ─────────────────────────────────────────────
  WeatherWidget: {
    intro: {
      title: 'מזג האוויר',
      emoji: '⛅',
      description: 'תחזית מזג אוויר לפי מיקום בית הכנסת.',
    },
    defaultData: {
      title: 'מזג אוויר', showTitle: false,
      bgColor: '', borderType: 'default',
    },
    tabs: [],
  },

  // ─────────────────────────────────────────────
  ClockWidget: {
    intro: {
      title: 'שעון',
      emoji: '🕐',
      description: 'שעון דיגיטלי או אנלוגי המציג את השעה בזמן אמת.',
    },
    defaultData: {
      bgColor: '', borderType: 'default',
      textColor: '#1f3a5f',
    },
    showTitleSection: false,
    tabs: [
      { id: 'settings', label: 'הגדרות', componentName: 'ClockSettingsStep' },
    ],
  },

  // ─────────────────────────────────────────────
  NoticeBoardWidget: {
    intro: {
      title: 'לוח מודעות',
      emoji: '📋',
      description: 'לוח מודעות דיגיטלי לעדכונים לקהל.',
    },
    defaultData: {
      title: 'לוח מודעות', showTitle: true,
      bgColor: '', borderType: 'default',
      notices: [],
    },
    tabs: [
      { id: 'notices', label: 'מודעות', componentName: 'GenericListStep', props: {
        fieldKey: 'notices',
        addLabel: 'הוסף מודעה',
        emptyLabel: 'אין מודעות עדיין',
        fields: [
          { key: 'content',    label: 'תוכן המודעה',       type: 'richtext',       required: true },
          { key: 'expiryDate', label: 'תפוגה (אופציונלי)', type: 'datetime-local', required: false },
        ],
      }},
    ],
  },

  // ─────────────────────────────────────────────
  EmergencyWidget: {
    intro: {
      title: 'כוחות חירום',
      emoji: '🚑',
      description: 'מציג רשימת מתנדבי חירום של הקהילה.',
    },
    defaultData: {
      title: 'חירום והצלה', showTitle: true,
      bgColor: '', borderType: 'default',
      emergencyData: { volunteers: { medic: [], paramedic: [], maar: [] } },
    },
    tabs: [
      { id: 'volunteers', label: 'מתנדבים', componentName: 'EmergencyStep' },
    ],
  },

  // ─────────────────────────────────────────────
  ContactsWidget: {
    intro: {
      title: 'אנשי קשר',
      emoji: '📞',
      description: 'ספר טלפונים של עובדי ופעילי הקהילה.',
    },
    defaultData: {
      title: 'אנשי קשר והנהלה', showTitle: true,
      bgColor: '', borderType: 'default',
      contacts: [],
    },
    tabs: [
      { id: 'contacts', label: 'אנשי קשר', componentName: 'GenericListStep', props: {
        fieldKey: 'contacts',
        addLabel: 'הוסף איש קשר',
        emptyLabel: 'אין אנשי קשר עדיין',
        fields: [
          { key: 'name',          label: 'שם',          type: 'text', placeholder: 'שם מלא', required: true },
          { key: 'role',          label: 'תפקיד',       type: 'text', placeholder: 'רב, גבאי, מנהל...' },
          { key: 'phone',         label: 'טלפון',       type: 'tel',  placeholder: '050-0000000' },
          { key: 'availableTime', label: 'שעות זמינות', type: 'text', placeholder: 'ימים א-ה, 9:00-17:00' },
        ],
      }},
    ],
  },

  // ─────────────────────────────────────────────
  SimchaWidget: {
    intro: {
      title: 'שמחות הקהילה',
      emoji: '🎊',
      description: 'מציג שמחות ואירועי קהילה.',
    },
    defaultData: {
      title: 'שמחות בקהילה', showTitle: true,
      bgColor: '', borderType: 'default',
      simchas: [],
      showTopGreeting: false,
      topGreeting: 'הקהילה מאחלת מזל טוב לכל בעלי השמחות!',
      topBgColor: '', topTextColor: '', topFontFamily: 'inherit', topFontSize: 'medium', topSymbol: '✦',
      showBottomGreeting: false,
      bottomGreeting: 'ויזכו לאורך ימים ושנים טובות, אמן!',
      bottomBgColor: '', bottomTextColor: '', bottomFontFamily: 'inherit', bottomFontSize: 'medium', bottomSymbol: '✦',
    },
    tabs: [
      { id: 'simchas', label: 'שמחות', componentName: 'GenericListStep', props: {
        fieldKey: 'simchas',
        addLabel: 'הוסף שמחה',
        emptyLabel: 'אין שמחות עדיין',
        fields: [
          { key: 'name',           label: 'שם המשפחה',                type: 'text', placeholder: 'כהן, לוי...', required: true },
          { key: 'celebrant',      label: 'שם הבן / בת / חתן / כלה', type: 'text', placeholder: 'משה, רחל, דוד ושרה...' },
          { key: 'type',           label: 'סוג שמחה', type: 'select', options: [
            { value: 'wedding_boy',     label: 'חתונה בן' },
            { value: 'wedding_girl',    label: 'חתונה בת' },
            { value: 'bar_mitzvah',     label: 'בר מצווה' },
            { value: 'birth_boy',       label: 'הולדת בן' },
            { value: 'birth_girl',      label: 'הולדת בת' },
            { value: 'grandchild_boy',  label: 'הולדת נכד' },
            { value: 'grandchild_girl', label: 'הולדת נכדה' },
            { value: 'engagement_boy',  label: 'אירוסין בן' },
            { value: 'engagement_girl', label: 'אירוסין בת' },
            { value: 'other',           label: 'אחר' },
          ]},
          { key: 'customType',     label: 'פרט את סוג השמחה', type: 'text', placeholder: 'שם השמחה...', showWhen: { field: 'type', value: 'other' } },
          { key: 'customTypeBold', label: 'מילים להדגשה', type: 'wordBoldSelector', linkedField: 'customType', showWhen: { field: 'type', value: 'other' } },
          { key: 'additionalText', label: 'טקסט נוסף',        type: 'text', placeholder: 'פרטים נוספים...' },
          { key: 'expiryDate',     label: 'תאריך סיום הצגה (אופציונלי)', type: 'datetime-local', required: false },
        ],
      }},
      { id: 'greetings', label: 'ברכות', componentName: 'SimchaGreetingsTab' },
    ],
  },

  // ─────────────────────────────────────────────
  NitzavSpotlightWidget: {
    intro: {
      title: 'נציב היום',
      emoji: '✨',
      description: 'מדגיש אדם מיוחד — תורם, מנציח, אדם לכבוד.',
    },
    defaultData: {
      title: 'נציב הקהילה', showTitle: false,
      bgColor: '', borderType: 'default',
      nitzavData: { name: 'תורם הקהילה', dedication: 'לעילוי נשמת', type: 'נציב היום', iconId: 'star' },
    },
    tabs: [
      { id: 'nitzav', label: 'פרטי הנציב', componentName: 'NitzavStep' },
    ],
  },

  // ─────────────────────────────────────────────
  ExpensesAnalyticsWidget: {
    intro: {
      title: 'ניתוח הוצאות',
      emoji: '📊',
      description: 'גרף הוצאות לפי קטגוריות.',
    },
    defaultData: {
      title: 'פילוח הוצאות', showTitle: true,
      bgColor: '', borderType: 'default',
      categories: [{ name: 'תחזוקה', amount: 5000, color: '#4ECDC4' }],
      freeText: '',
    },
    tabs: [
      { id: 'categories', label: 'קטגוריות', componentName: 'GenericListStep', props: {
        fieldKey: 'categories',
        addLabel: 'הוסף קטגוריה',
        emptyLabel: 'אין קטגוריות עדיין',
        fields: [
          { key: 'name',   label: 'שם הקטגוריה', type: 'text',   placeholder: 'תחזוקה, חשמל...', required: true },
          { key: 'amount', label: 'סכום (₪)',     type: 'number', placeholder: '5000' },
          { key: 'color',  label: 'צבע',          type: 'color' },
        ],
      }},
    ],
  },

  // ─────────────────────────────────────────────
  FinancialGaugeWidget: {
    intro: {
      title: 'מדד כלכלי',
      emoji: '💰',
      description: 'מציג את יחס ההכנסות מול ההוצאות בצורת מד.',
    },
    defaultData: {
      title: 'תמונת מצב כספית', showTitle: true,
      bgColor: '', borderType: 'default',
      incomeData: [], expensesData: [],
    },
    tabs: [
      { id: 'income',   label: 'הכנסות', componentName: 'FinancialItemsStep', props: { field: 'incomeData',   sectionLabel: 'הכנסות', color: '#1e3a5f' } },
      { id: 'expenses', label: 'הוצאות', componentName: 'FinancialItemsStep', props: { field: 'expensesData', sectionLabel: 'הוצאות', color: '#e6c45e' } },
    ],
  },

  // ─────────────────────────────────────────────
  ParnasWidget: {
    intro: {
      title: 'פרנס קהילה',
      emoji: '🕍',
      description: 'ניהול רשימת פרנסים עם שיוך יומי, שבועי, או חודשי.',
    },
    defaultData: {
      title: 'פרנס הקהילה', showTitle: false,
      bgColor: '', borderType: 'default',
      parnasType: 'maor', parnasTypeCustomLabel: '', parnasTypeIcon: 'star',
      sponsors: [],
      tileColors: { daily: '#b8860b', weekly: '#0d7377', monthly: '#1e3a5f' },
      placeholderName: 'השם שלך כאן',
      placeholderNote: 'לתרומות ניתן לפנות לגבאי בית הכנסת',
      weeklyYearDisplayYear: null,
      weeksAheadCount: 8,
      honorCardPriority: 'auto',
      includedSlotTypes: { daily: true, weekly: true, monthly: true },
    },
    tabs: [
      { id: 'parnasType', label: 'סוג פרנסות', componentName: 'ParnasTypeStep' },
      { id: 'sponsors',   label: 'פרנסים',     componentName: 'SponsorsStep' },
    ],
  },

  // ─────────────────────────────────────────────
  NextMinyaWidget: {
    intro: {
      title: 'מניין הבא',
      emoji: '🕍',
      description: 'מציג בולט את המניין הקרוב ביותר — שעה, שם ותזמון — על ידי משיכת זמנים מכל רכיבי התפילות בלוח.',
    },
    defaultData: {
      title: 'מניין הבא', showTitle: true,
      bgColor: '', borderType: 'default',
      tefilotColor: '#0369a1',
      moadColor: '#b45309',
      shabbatColor: '#7c3aed',
    },
    tabs: [
      { id: 'colors', label: 'צבעים', componentName: 'ZmanimDesignStep' },
    ],
  },
};

/**
 * getWidgetTabsConfig(componentKey)
 * מחזיר את הקונפיג של רכיב נתון, או null אם לא קיים.
 */
export const getWidgetTabsConfig = (componentKey) =>
  WIDGET_TABS_CONFIG[componentKey] || null;
